import React, { useState, useEffect, useMemo } from 'react';
import { TrendingUp, Search, ChevronUp, ChevronDown } from 'lucide-react';
import { clsx } from 'clsx';
import { PermissionGuard } from '@components/PermissionGuard';
import { financialService } from '@services/financialService';
import type { ProductEarnRow } from '@/types/financial';
import { formatCurrencyBRL, formatEarnPercent, formatQuantityBR } from './financialUtils';
import { LoadingSpinner, ErrorBox, PermissionFallback, SummaryCard, inputCls } from './financialShared';

type SortColumn = keyof ProductEarnRow;
type SortDirection = 'asc' | 'desc';

interface ColumnDef {
  key: SortColumn;
  label: string;
  align: 'left' | 'right';
}

const COLUMNS: ColumnDef[] = [
  { key: 'sku', label: 'SKU', align: 'left' },
  { key: 'description', label: 'Descrição', align: 'left' },
  { key: 'price', label: 'Preço', align: 'right' },
  { key: 'cost', label: 'Custo', align: 'right' },
  { key: 'stockQuantityAvailable', label: 'Estoque Disponível', align: 'right' },
  { key: 'earn', label: 'Margem', align: 'right' },
];

export const ProductEarnView: React.FC = () => {
  const [rows, setRows] = useState<ProductEarnRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [sortColumn, setSortColumn] = useState<SortColumn>('earn');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await financialService.getProductEarn();
      setRows(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar margem de produtos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const visibleRows = useMemo(() => {
    const term = filter.trim().toLowerCase();
    const filtered = term
      ? rows.filter((r) => r.sku.toLowerCase().includes(term) || (r.description ?? '').toLowerCase().includes(term))
      : rows;

    const sorted = [...filtered].sort((a, b) => {
      const va = a[sortColumn];
      const vb = b[sortColumn];
      // Estoque/descrição podem vir null da API: ficam sempre no fim, em qualquer direção.
      if (va == null || vb == null) return va == null ? (vb == null ? 0 : 1) : -1;
      let cmp: number;
      if (typeof va === 'number' && typeof vb === 'number') {
        cmp = va - vb;
      } else {
        cmp = String(va).localeCompare(String(vb), 'pt-BR');
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [rows, filter, sortColumn, sortDirection]);

  const averageEarn = useMemo(() => {
    if (visibleRows.length === 0) return 0;
    return visibleRows.reduce((sum, r) => sum + r.earn, 0) / visibleRows.length;
  }, [visibleRows]);

  return (
    <PermissionGuard permission="financial.product_earn.read" fallback={<PermissionFallback label="a margem de produtos" />}>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Margem de Produtos</h1>
          <p className="text-sm text-slate-500 mt-1">Relação preço/custo dos produtos ativos</p>
        </div>

        {/* Filtro */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
          <div className="relative max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              className={inputCls + ' pl-9'}
              placeholder="Filtrar por SKU ou descrição..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
        </div>

        {error && <ErrorBox message={error} onRetry={load} />}
        {loading && <LoadingSpinner />}

        {!loading && !error && (
          <>
            {/* Resumo */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <SummaryCard label="Produtos" value={String(visibleRows.length)} />
              <SummaryCard
                label="Margem Média"
                value={formatEarnPercent(averageEarn)}
                valueClassName="text-emerald-600"
              />
            </div>

            {/* Tabela */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      {COLUMNS.map((column) => (
                        <th
                          key={column.key}
                          className={clsx(
                            'py-2 px-3 font-semibold text-slate-600 cursor-pointer hover:bg-slate-100 select-none',
                            column.align === 'right' ? 'text-right' : 'text-left',
                          )}
                          onClick={() => handleSort(column.key)}
                        >
                          <span className="inline-flex items-center gap-1">
                            {column.label}
                            {sortColumn === column.key &&
                              (sortDirection === 'asc' ? (
                                <ChevronUp size={13} className="text-emerald-600" />
                              ) : (
                                <ChevronDown size={13} className="text-emerald-600" />
                              ))}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.length === 0 && (
                      <tr>
                        <td colSpan={COLUMNS.length} className="py-10 text-center text-slate-400">
                          Nenhum produto encontrado
                        </td>
                      </tr>
                    )}
                    {visibleRows.map((row) => (
                      <tr key={row.sku} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-2 px-3 font-mono text-xs text-slate-500">{row.sku}</td>
                        <td className="py-2 px-3 text-slate-700">{row.description}</td>
                        <td className="py-2 px-3 text-right whitespace-nowrap text-slate-700">{formatCurrencyBRL(row.price)}</td>
                        <td className="py-2 px-3 text-right whitespace-nowrap text-slate-700">{formatCurrencyBRL(row.cost)}</td>
                        <td className="py-2 px-3 text-right text-slate-700">{formatQuantityBR(row.stockQuantityAvailable)}</td>
                        <td
                          className={clsx(
                            'py-2 px-3 text-right font-medium whitespace-nowrap',
                            row.earn >= 1 ? 'text-emerald-600' : 'text-red-600',
                          )}
                        >
                          <span className="inline-flex items-center gap-1">
                            <TrendingUp size={13} />
                            {formatEarnPercent(row.earn)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </PermissionGuard>
  );
};

export default ProductEarnView;
